import { useMemo, useRef, useSyncExternalStore } from 'react';

import { liveQuery } from 'dexie';

import { apiGet, apiPatch, apiPost } from './api_v2';
import { isImageContentType, resolveUploadContentType } from './attachments';
import db from './db';
import { logDebug, logError, logWarn } from './logger';
import { relayMediaAttachmentProcessing, isRelayableMediaContentType } from './media_attachment_relay';
import { currentWorkspaceId, getMetaContent } from './meta';
import { contentDispositionHeader, isPresignedUrlUsable } from './presignedUpload';
import { generateLocalThumbnailData } from './thumbdata';
import { subscribeWorkspaceIdScope } from './workspaceIdScope';

const STORE_TAG = 'media-attachments-store';
export const ENQUEUE_UPLOAD_EVENT = 'media-uploads:enqueue';
export const ENQUEUE_UPLOAD_RESULT_EVENT = 'media-uploads:queued';
export const ENQUEUE_UPLOAD_ERROR_EVENT = 'media-uploads:enqueue-error';

export const sortByTimestamp = (fieldName) => (a, b) => {
  const aTime = a?.[fieldName] || 0;
  const bTime = b?.[fieldName] || 0;
  return aTime - bTime;
};

export const MediaAttachmentStates = Object.freeze({
  pending: 'pending',
  uploading: 'uploading',
  processing: 'processing',
  retrying: 'retrying',
  completed: 'completed',
  error: 'error',
});

export const MediaAttachmentOwnerTypes = Object.freeze({
  composer: 'composer',
  optimisticMessage: 'optimistic_message',
});

export function isComposerOwnedMediaAttachment(record = {}) {
  const ownerType = record?.owner_type;
  return ownerType == null || ownerType === MediaAttachmentOwnerTypes.composer;
}

function matchesMediaAttachmentOwner(record = {}, { ownerType = null, ownerId = null } = {}) {
  if (ownerType != null) {
    if (ownerType === MediaAttachmentOwnerTypes.composer) {
      if (!isComposerOwnedMediaAttachment(record)) return false;
    } else if (record?.owner_type !== ownerType) {
      return false;
    }
  }

  if (ownerId != null && record?.owner_id !== ownerId) return false;
  return true;
}

// Actively uploading or waiting to retry
export function isUploadActiveState(state) {
  return state === MediaAttachmentStates.pending || state === MediaAttachmentStates.uploading || state === MediaAttachmentStates.retrying;
}

// In the first upload attempt (not a retry)
export function isUploadInflightState(state) {
  return state === MediaAttachmentStates.pending || state === MediaAttachmentStates.uploading;
}

let started = false;
let subscription = null;
let subscribedWorkspaceId = null;

const listeners = new Set();
const EMPTY_MEDIA_ATTACHMENTS = new Map();
// Immutable snapshot for subscribers; replaced on every update
let frozenMediaAttachments = new Map();
const inflightUploads = new Map();
const retryTimeouts = new Map();
// Session-long cache of uploadId -> workspaceId so pipeline ops (resolveWorkspaceId)
// keep targeting the right PK after the active workspace changes.
const uploadWorkspaceIds = new Map();
// Bounded adaptive schedule: fast polls while processing likely completes, then
// settle at the sustained 1.5s rate for slow (video/audio) jobs. Index is clamped
// to the final entry, so later delays stay at 1.5s.
const REMOTE_ATTACHMENT_POLL_INTERVALS_MS = [
  250, 250, 250, 250, 250, 250, 250, 250,
  500,
  1000,
  1500,
];
const UPLOAD_RETRY_DELAY_MS = 2000;
// Presigned PUT URLs carry the SDK's default 15-minute expiry. A stored URL this close
// to expiring is re-signed instead of reused on resume or retry.
const PRESIGNED_URL_MIN_REMAINING_MS = 60 * 1000;
// A rejected PUT (expired or mismatched signature) is re-signed this many times on its
// own before it is treated as a real failure; a user-initiated retry resets the budget.
const MAX_PRESIGN_ATTEMPTS = 2;

function rememberUploadWorkspace(uploadId, workspaceId) {
  if (uploadId && workspaceId) uploadWorkspaceIds.set(uploadId, workspaceId);
}

// media_attachments PK is `id` only; workspace_id may be null (server JSON). Prefer the
// session cache so in-flight uploads started in workspace A keep resolving to A after switch.
function resolveWorkspaceId(uploadId) {
  return uploadWorkspaceIds.get(uploadId) ?? frozenMediaAttachments.get(uploadId)?.workspace_id ?? null;
}

async function mediaAttachmentsByWorkspaceAndSession(workspaceId, sessionKey) {
  if (!sessionKey) return [];
  const rows = await db.media_attachments.where('session_key').equals(sessionKey).toArray();
  return workspaceId ? rows.filter((r) => r.workspace_id === workspaceId) : rows;
}

// Blob storage cleanup helpers
async function deleteBlobStorage(workspaceId, uploadId) {
  try {
    await db._pending_upload_blobs.delete([workspaceId, uploadId]);
  } catch (error) {
    logWarn(STORE_TAG, 'failed to delete from blob storage', { uploadId, error });
  }
}

async function deleteBlobStorageBulk(keys) {
  try {
    await db._pending_upload_blobs.bulkDelete(keys);
  } catch (error) {
    logWarn(STORE_TAG, 'failed to bulk delete from blob storage', { error });
  }
}

function notify() {
  listeners.forEach((listener) => listener());
}

function setMediaAttachments(entries) {
  // Shallow-compare: skip notify if nothing actually changed.
  if (frozenMediaAttachments.size === entries.size) {
    let same = true;
    for (const [key, value] of entries) {
      if (frozenMediaAttachments.get(key) !== value) {
        same = false;
        break;
      }
    }
    if (same) return;
  }
  frozenMediaAttachments = new Map(entries);
  notify();
}

function clearRetryTimeout(uploadId) {
  const scheduled = retryTimeouts.get(uploadId);
  if (!scheduled) return;

  window.clearTimeout(scheduled.timeoutId);
  retryTimeouts.delete(uploadId);
}

function syncRetryTimeouts(rows) {
  const liveUploadIds = new Set(rows.map((row) => row?.id).filter(Boolean));

  retryTimeouts.forEach((_scheduled, uploadId) => {
    if (!liveUploadIds.has(uploadId)) {
      clearRetryTimeout(uploadId);
    }
  });
}

function scheduleRetryTimeout(uploadId, nextRetryAt) {
  if (!uploadId) return;

  const normalizedNextRetryAt = Number.isFinite(Number(nextRetryAt)) ? Number(nextRetryAt) : Date.now() + UPLOAD_RETRY_DELAY_MS;
  const existing = retryTimeouts.get(uploadId);
  if (existing?.nextRetryAt === normalizedNextRetryAt) return;

  clearRetryTimeout(uploadId);

  const delay = Math.max(0, normalizedNextRetryAt - Date.now());
  const timeoutId = window.setTimeout(() => {
    retryTimeouts.delete(uploadId);
    startUpload(uploadId).catch((error) => {
      logError(STORE_TAG, 'failed to resume retrying upload', { uploadId, error });
    });
  }, delay);

  retryTimeouts.set(uploadId, { timeoutId, nextRetryAt: normalizedNextRetryAt });
}

// Once the bytes are up, an error from the process/poll stage usually means the
// attachment finished server-side and only the client lost track. Resume it once per
// session; anything else waits for the user's retry.
const autoResumedUploadIds = new Set();

function resumeErroredProcessing(row) {
  if (!row?.attachment_id || row.resume_state !== MediaAttachmentStates.processing) return;
  if (autoResumedUploadIds.has(row.id) || inflightUploads.has(row.id)) return;

  autoResumedUploadIds.add(row.id);
  retryMediaAttachmentsByUploadIds([row.id]).catch((error) => {
    logError(STORE_TAG, 'failed to resume errored processing', { uploadId: row.id, error });
  });
}

function handleLiveQueryUpdate(rows) {
  const next = new Map();
  rows.forEach((row) => {
    next.set(row.id, row);
    if (row.workspace_id) rememberUploadWorkspace(row.id, row.workspace_id);
  });
  setMediaAttachments(next);
  syncRetryTimeouts(rows);

  rows.forEach((row) => {
    if (!row) return;
    if (row.upload?.state === MediaAttachmentStates.completed) {
      clearRetryTimeout(row.id);
      return;
    }

    if (row.upload?.state === MediaAttachmentStates.error) {
      clearRetryTimeout(row.id);
      resumeErroredProcessing(row);
      return;
    }

    if (inflightUploads.has(row.id)) return;

    if (row.upload?.state === MediaAttachmentStates.retrying) {
      // Retry timing is persisted in Dexie so page reloads don't lose pending retries.
      scheduleRetryTimeout(row.id, row.next_retry_at);
      return;
    }

    if (row.upload?.state === MediaAttachmentStates.processing && row.attachment_id) {
      startUpload(row.id).catch((error) => {
        logError(STORE_TAG, 'failed to resume processing from liveQuery', { uploadId: row.id, error });
      });
      return;
    }

    // Check if file exists in IndexedDB before starting upload
    const checkFileAndStartUpload = async () => {
      try {
        const stored = await db._pending_upload_blobs.get([row.workspace_id, row.id]);
        if (!stored?.raw_file) {
          await failMediaAttachment(row.id, 'file-unavailable');
          return;
        }
      } catch (error) {
        logError(STORE_TAG, 'failed to check file in IndexedDB', { uploadId: row.id, error });
        await failMediaAttachment(row.id, 'file-unavailable');
        return;
      }
      await startUpload(row.id);
    };

    checkFileAndStartUpload().catch((error) => {
      logError(STORE_TAG, 'failed to start upload from liveQuery', { uploadId: row.id, error });
    });
  });
}

function handleLiveQueryError(error) {
  logError(STORE_TAG, 'liveQuery error', error);
}

function handleEnqueueEvent(event) {
  const detail = event?.detail || {};
  const { file, purpose, sessionKey, metadata, requestId, channelId, ownerType, ownerId, workspaceId } = detail;

  if (!file) {
    logWarn(STORE_TAG, 'enqueue event missing file detail', detail);
    if (requestId) {
      window.dispatchEvent(new CustomEvent(ENQUEUE_UPLOAD_ERROR_EVENT, { detail: { requestId, error: 'missing-file' } }));
    }
    return;
  }

  // Queue the file upload; editing flows ignore attachments so no edit context is passed.
  enqueueUpload({ file, purpose, sessionKey, metadata, channelId, ownerType, ownerId, workspaceId })
    .then((uploadId) => {
      if (requestId) {
        window.dispatchEvent(new CustomEvent(ENQUEUE_UPLOAD_RESULT_EVENT, { detail: { requestId, uploadId } }));
      }
    })
    .catch((error) => {
      logError(STORE_TAG, 'failed to enqueue via event', error);
      if (requestId) {
        window.dispatchEvent(new CustomEvent(ENQUEUE_UPLOAD_ERROR_EVENT, { detail: { requestId, error: error?.message } }));
      }
    });
}

// Re-scopes the liveQuery to the active workspace. Called on start and whenever
// the routed app publishes `output:internal:workspace-id-scope` with a new id —
// without this, a workspace switch would leave the prior workspace's rows
// visible via useAllMediaAttachments.
function restartWorkspaceSubscription(nextWorkspaceId = currentWorkspaceId()) {
  if (subscribedWorkspaceId === nextWorkspaceId && subscription) return;

  subscribedWorkspaceId = nextWorkspaceId;
  subscription?.unsubscribe();
  subscription = null;

  const workspaceId = nextWorkspaceId;
  if (!workspaceId) {
    setMediaAttachments(new Map());
    return;
  }

  subscription = liveQuery(async () => db.media_attachments.where('workspace_id').equals(workspaceId).toArray()).subscribe({
    next: handleLiveQueryUpdate,
    error: handleLiveQueryError,
  });
}

function ensureStarted() {
  if (started) return;
  started = true;

  restartWorkspaceSubscription(currentWorkspaceId());
  subscribeWorkspaceIdScope((workspaceId) => restartWorkspaceSubscription(workspaceId));

  try {
    window.addEventListener(ENQUEUE_UPLOAD_EVENT, handleEnqueueEvent);
  } catch (error) {
    logWarn(STORE_TAG, 'failed to attach enqueue listener', error);
  }
}

export function subscribe(listener) {
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && subscription) {
      // keep subscription alive for the session; no teardown to avoid churn
    }
  };
}

export function getSnapshot() {
  ensureStarted();
  return frozenMediaAttachments;
}

export function useAllMediaAttachments(matchers = {}) {
  ensureStarted();

  const matcherEntries = useMemo(() => Object.entries(matchers), [matchers]);
  const hasMatchers = matcherEntries.length > 0;

  return useSyncExternalStore(
    subscribe,
    useMemo(() => {
      if (!hasMatchers) return getSnapshot;

      let cachedSnapshot = null;
      let cachedResult = null;

      return () => {
        const currentSnapshot = getSnapshot();
        if (currentSnapshot === cachedSnapshot) {
          return cachedResult;
        }

        cachedSnapshot = currentSnapshot;
        const filtered = new Map();
        currentSnapshot.forEach((mediaAttachment, id) => {
          const matchesAll = matcherEntries.every(([key, value]) => {
            if (value === undefined || value === null) return true;
            return mediaAttachment[key] === value;
          });
          if (matchesAll) filtered.set(id, mediaAttachment);
        });
        cachedResult = filtered;
        return filtered;
      };
    }, [hasMatchers, matcherEntries]),
    () => new Map(),
  );
}

export function useMediaAttachments(uploadIds = []) {
  ensureStarted();
  const rawIds = Array.isArray(uploadIds) ? uploadIds.filter(Boolean) : [];
  const idsKey = rawIds.join(':');
  const stableIdsRef = useRef({ key: '', ids: [] });

  // Keep a stable array ref when the contents haven't changed so the
  // useSyncExternalStore snapshot function stays memoized across rerenders.
  if (stableIdsRef.current.key !== idsKey) {
    stableIdsRef.current = {
      key: idsKey,
      ids: rawIds,
    };
  }

  const ids = stableIdsRef.current.ids;

  return useSyncExternalStore(
    subscribe,
    useMemo(() => {
      let cachedSnapshot = null;
      let cachedSubset = null;

      return () => {
        const snapshot = getSnapshot();
        if (ids.length === 0) return EMPTY_MEDIA_ATTACHMENTS;

        // useSyncExternalStore needs the same object ref until the store changes.
        if (snapshot === cachedSnapshot) {
          return cachedSubset;
        }

        cachedSnapshot = snapshot;
        const subset = new Map();
        ids.forEach((id) => {
          if (snapshot.has(id)) subset.set(id, snapshot.get(id));
        });
        cachedSubset = subset;
        return subset;
      };
    }, [ids]),
    () => EMPTY_MEDIA_ATTACHMENTS,
  );
}

export function useMediaAttachment(uploadId) {
  ensureStarted();
  return useSyncExternalStore(
    subscribe,
    useMemo(() => {
      let cachedSnapshot = null;
      let cachedResult = null;
      return () => {
        const snapshot = getSnapshot();
        if (snapshot === cachedSnapshot) return cachedResult;
        cachedSnapshot = snapshot;
        cachedResult = uploadId ? snapshot.get(uploadId) || null : null;
        return cachedResult;
      };
    }, [uploadId]),
    () => null,
  );
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function buildUploadError(message, { status = null, retryable = null, resumeState = null, stage = null } = {}) {
  const error = new Error(message);
  if (status !== null) error.status = status;
  if (retryable !== null) error.retryable = retryable;
  if (resumeState) error.resumeState = resumeState;
  if (stage) error.stage = stage;
  return error;
}

function annotateUploadError(error, { resumeState = null } = {}) {
  if (resumeState && !error.resumeState) error.resumeState = resumeState;
  return error;
}

function getUploadErrorMessage(error) {
  return error?.message || 'unknown-error';
}

function extractUploadErrorStatus(error) {
  return Number.isFinite(error?.status) ? error.status : null;
}

function isRetryableUploadStatus(status) {
  return status === 429 || status >= 500;
}

function isRetryableUploadError(error) {
  if (error?.retryable === true) return true;

  const status = extractUploadErrorStatus(error);
  if (Number.isFinite(status) && isRetryableUploadStatus(status)) return true;

  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('network request failed') ||
    message.includes('upload-network-error')
  );
}

// Safely extract JSON from a response that may be a standard fetch Response
// (where .json is a method) or a pre-parsed object (where .json is already a value).
// Returns null on parse failure so callers can throw their own typed errors.
async function readResponseJson(response) {
  try {
    if (typeof response?.json === 'function') return await response.json();
    return await response?.json;
  } catch {
    return null;
  }
}

function readResponseStatus(response) {
  const candidates = [response?.status, response?.statusCode, response?.response?.status];

  for (const candidate of candidates) {
    if (Number.isFinite(candidate)) return candidate;
  }

  return null;
}

// Unwraps a media_attachments API response (used by both create and show) into
// the inner `media_attachment` object, or throws a typed upload error the upload
// pipeline knows how to classify (retryable vs. fatal, which stage to resume from).
//
// Both endpoints share the same { data: { media_attachment } } success envelope
// and the same { errors | error } failure shape, so keeping the parsing in one
// place means retry/status/resume-state handling can't drift between them.
// The caller supplies the per-endpoint bits that legitimately differ:
//   - defaultErrorMessage:     fallback when the server response has no error body
//   - invalidResponseMessage:  thrown when the envelope is missing the expected fields
//   - resumeState:             which pipeline stage a retry should resume from
async function parseMediaAttachmentResponse(response, { defaultErrorMessage, invalidResponseMessage, resumeState }) {
  if (!response.ok) {
    const status = readResponseStatus(response);
    const errorBody = await readResponseJson(response);
    throw buildUploadError(errorBody?.errors ? JSON.stringify(errorBody.errors) : errorBody?.error || defaultErrorMessage, {
      status,
      retryable: isRetryableUploadStatus(status),
      resumeState,
    });
  }

  const payload = await readResponseJson(response);
  const attachment = payload?.data?.media_attachment;
  if (!attachment?.id) throw new Error(invalidResponseMessage);
  return attachment;
}

async function runUploadStage(resumeState, callback) {
  try {
    return await callback();
  } catch (error) {
    throw annotateUploadError(error, { resumeState });
  }
}

async function writeMediaAttachment(uploadId, changes, { merge = true } = {}) {
  if (!uploadId) return;
  ensureStarted();

  const updates = changes ?? {};

  // PK is a UUID for new uploads, or the real attachment id when editing.
  // Either way we never use the id field until we have a server-side attachment id.
  delete updates.id;

  if (merge) {
    const existing = await db.media_attachments.get(uploadId);
    if (!existing) return;
    const next = { ...existing, ...updates };
    if (next.workspace_id == null && existing.workspace_id != null) next.workspace_id = existing.workspace_id;
    await db.media_attachments.put(next);
  } else {
    const record = { ...updates, id: uploadId, workspace_id: updates.workspace_id ?? resolveWorkspaceId(uploadId) };
    await db.media_attachments.put(record);
  }
}

export async function enqueueUpload({
  file,
  purpose = null,
  sessionKey = null,
  metadata = null,
  channelId = null,
  ownerType = null,
  ownerId = null,
  workspaceId = currentWorkspaceId(),
}) {
  ensureStarted();

  // media_attachments and _pending_upload_blobs both require a workspace_id; refuse
  // to write a record scoped to nothing rather than producing an unreachable row.
  if (!workspaceId) throw new Error('missing-workspace-id');

  const uploadId = crypto.randomUUID();
  let thumbdata = null;
  let thumbnailBlob = null;

  // Generate thumbnail data for images
  if (file?.type?.toLowerCase().startsWith('image/')) {
    try {
      const result = await generateLocalThumbnailData(file);
      thumbdata = { thumbhash: result.thumbhash, thumbsize: result.thumbsize };
      thumbnailBlob = result.thumbnailBlob;
    } catch (error) {
      logWarn(STORE_TAG, 'failed to generate thumbnail, storing raw file only', error);
    }
  }

  try {
    await db._pending_upload_blobs.put({
      workspace_id: workspaceId,
      upload_id: uploadId,
      raw_file: file,
      local_thumbnail: thumbnailBlob,
    });
  } catch (error) {
    logError(STORE_TAG, 'failed to persist upload blob to IndexedDB', { uploadId, error });
    throw new Error('upload-blob-store-failed');
  }

  // Capture file properties now — IndexedDB's structured clone drops own properties
  // (e.g. file.type from drag-and-drop), so they'd be lost after retrieval.
  const recordMetadata = {
    file_name: file?.name ?? null,
    content_type: resolveUploadContentType(file),
    content_length: file?.size ?? null,
    thumbhash: thumbdata?.thumbhash ?? null,
    thumbsize: thumbdata?.thumbsize ?? null,
    ...metadata,
  };

  const record = {
    id: uploadId,
    purpose,
    workspace_id: workspaceId,
    session_key: sessionKey,
    owner_type: ownerType,
    owner_id: ownerId,
    channel_id: channelId,
    metadata: recordMetadata,
    upload: { state: MediaAttachmentStates.pending, progress: 0 },
    error: null,
    retry_count: 0,
    next_retry_at: null,
    resume_state: MediaAttachmentStates.pending,
    variants: null,
    written_at: Math.floor(Date.now() / 1000),
  };

  try {
    await db.media_attachments.put(record);
  } catch (error) {
    logError(STORE_TAG, 'failed to persist upload record to IndexedDB', { uploadId, error });
    await deleteBlobStorage(workspaceId, uploadId);
    throw new Error('upload-record-store-failed');
  }
  rememberUploadWorkspace(uploadId, workspaceId);
  logDebug(STORE_TAG, 'enqueue', uploadId, { purpose, workspaceId, sessionKey, ownerType, ownerId, channelId });

  startUpload(uploadId).catch((error) => {
    logError(STORE_TAG, 'failed to start upload after enqueue', { uploadId, error });
  });

  return uploadId;
}

export async function waitForMediaAttachmentUpload(uploadId, { timeoutMs = 120000, pollMs = 250 } = {}) {
  if (!uploadId) throw new Error('missing-upload-id');

  const deadline = Date.now() + timeoutMs;

  while (true) {
    const record = await db.media_attachments.get(uploadId);
    if (!record) throw new Error('upload-missing');

    const state = record.upload?.state;
    if (state === MediaAttachmentStates.completed && record.attachment_id) return record;
    if (state === MediaAttachmentStates.error) throw new Error(record.error || 'upload-failed');
    if (Date.now() > deadline) throw new Error('upload-timeout');

    await wait(pollMs);
  }
}

export async function getUploadFile(uploadId) {
  try {
    const stored = await db._pending_upload_blobs.get([resolveWorkspaceId(uploadId), uploadId]);
    return stored?.raw_file || null;
  } catch (error) {
    logWarn(STORE_TAG, 'failed to get upload file from IndexedDB', { uploadId, error });
    return null;
  }
}

export async function getLocalThumbnail(uploadId) {
  try {
    const stored = await db._pending_upload_blobs.get([resolveWorkspaceId(uploadId), uploadId]);
    return stored?.local_thumbnail || null;
  } catch (error) {
    logWarn(STORE_TAG, 'failed to get local thumbnail from IndexedDB', { uploadId, error });
    return null;
  }
}

export async function updateMediaAttachment(uploadId, changes) {
  await writeMediaAttachment(uploadId, changes, { merge: true });
}

function inferUploadResumeState(record = {}) {
  if (record?.resume_state) return record.resume_state;
  if (record?.attachment_id && record?.upload?.progress >= 100) return MediaAttachmentStates.processing;
  if (record?.attachment_id && record?.presigned_upload_url) return MediaAttachmentStates.uploading;
  return MediaAttachmentStates.pending;
}

async function setMediaAttachmentUploadingProgress(uploadId, progress) {
  if (!Number.isFinite(progress)) return;
  ensureStarted();

  const nextProgress = Math.min(100, Math.max(0, progress));
  const existing = await db.media_attachments.get(uploadId);

  // XHR progress events fire a lot — skip writes when the rounded value is the same.
  if (existing?.upload?.state === MediaAttachmentStates.uploading && existing?.upload?.progress === nextProgress) return;

  await updateMediaAttachment(uploadId, {
    error: null,
    next_retry_at: null,
    resume_state: MediaAttachmentStates.uploading,
    upload: {
      ...existing?.upload,
      state: MediaAttachmentStates.uploading,
      progress: nextProgress,
    },
  });
}

export async function failMediaAttachment(uploadId, error) {
  clearRetryTimeout(uploadId);
  const existing = await db.media_attachments.get(uploadId);
  const resumeState = error?.resumeState || inferUploadResumeState(existing);
  await updateMediaAttachment(uploadId, {
    upload: { state: MediaAttachmentStates.error },
    error: getUploadErrorMessage(error),
    next_retry_at: null,
    resume_state: resumeState,
  });
}

export async function retryMediaAttachment(uploadId, error, resumeState = MediaAttachmentStates.pending) {
  const existing = await db.media_attachments.get(uploadId);
  if (!existing) return;

  // TODO: exponential backoff would be nice, but a long offline stretch could
  // leave uploads stuck for ages after reconnect. Fixed delay keeps retries snappy.
  const nextRetryAt = Date.now() + UPLOAD_RETRY_DELAY_MS;
  const retryCount = Number.isFinite(Number(existing.retry_count)) ? Number(existing.retry_count) + 1 : 1;

  await updateMediaAttachment(uploadId, {
    error: getUploadErrorMessage(error),
    retry_count: retryCount,
    next_retry_at: nextRetryAt,
    resume_state: resumeState,
    upload: {
      ...existing?.upload,
      state: MediaAttachmentStates.retrying,
    },
  });

  scheduleRetryTimeout(uploadId, nextRetryAt);
}

async function removeMediaAttachmentsBulk(records) {
  if (!records.length) return;
  ensureStarted();

  records.forEach((record) => {
    clearRetryTimeout(record.id);
    uploadWorkspaceIds.delete(record.id);
  });
  const ids = records.map((record) => record.id);
  await db.media_attachments.bulkDelete(ids);

  // Treat row removal as a fallback for completion-time cleanup. A completed
  // local upload can still have a blob if its earlier IndexedDB deletion failed;
  // server-synced record ids simply won't match pending blob keys.
  const blobKeys = records
    .map((record) => {
      const ws = record.workspace_id ?? resolveWorkspaceId(record.id);
      return ws ? [ws, record.id] : null;
    })
    .filter(Boolean);
  if (blobKeys.length) await deleteBlobStorageBulk(blobKeys);
}

export async function removeMediaAttachment(uploadId) {
  if (!uploadId) return;
  ensureStarted();

  const existing = await db.media_attachments.get(uploadId);
  if (!existing) return;
  const workspaceId = resolveWorkspaceId(uploadId) ?? existing.workspace_id;
  clearRetryTimeout(uploadId);
  uploadWorkspaceIds.delete(uploadId);
  await db.media_attachments.delete(uploadId);
  if (workspaceId) await deleteBlobStorage(workspaceId, uploadId);
}

export async function removeMediaAttachmentsByUploadIds(uploadIds = []) {
  const normalizedUploadIds = Array.isArray(uploadIds) ? uploadIds.filter(Boolean) : [];
  if (!normalizedUploadIds.length) return;
  ensureStarted();

  const records = await db.media_attachments.where('id').anyOf(normalizedUploadIds).toArray();
  if (!records.length) return;
  await removeMediaAttachmentsBulk(records);
}

export async function removeEditMediaAttachmentsForChannel(channelId) {
  if (!channelId) return;
  ensureStarted();

  const workspaceId = currentWorkspaceId();
  if (!workspaceId) return;
  let records = await db.media_attachments
    .where('channel_id')
    .equals(channelId)
    .filter((record) => record.workspace_id === workspaceId)
    .toArray();
  records = records.filter((record) => record.session_key && record.session_key.includes(':edit-'));
  await removeMediaAttachmentsBulk(records);
}

export async function removeMediaAttachmentsForSessionKey(sessionKey, { ownerType = null, ownerId = null, workspaceId = currentWorkspaceId() } = {}) {
  if (!sessionKey) return;
  ensureStarted();

  if (!workspaceId) return;
  let records = await mediaAttachmentsByWorkspaceAndSession(workspaceId, sessionKey);
  records = records.filter((record) => matchesMediaAttachmentOwner(record, { ownerType, ownerId }));
  await removeMediaAttachmentsBulk(records);
}

export async function setMediaAttachmentsOwner({ uploadIds = [], ownerType, ownerId, sessionKey } = {}) {
  const normalizedUploadIds = Array.isArray(uploadIds) ? uploadIds.filter(Boolean) : [];
  if (!normalizedUploadIds.length) return;
  ensureStarted();

  const updates = {};
  if (ownerType !== undefined) updates.owner_type = ownerType;
  if (ownerId !== undefined) updates.owner_id = ownerId;
  if (sessionKey !== undefined) updates.session_key = sessionKey;
  if (Object.keys(updates).length === 0) return;

  // Ownership transfer only patches the requested fields. A full record replace here
  // would race with the upload pipeline and lose progress/state.
  await db.media_attachments.where('id').anyOf(normalizedUploadIds).modify(updates);
}

// One-shot read for submission. For live reads, use useAllMediaAttachments.
export async function getMediaAttachmentsForSession({
  channelId,
  sessionKey,
  uploadState = MediaAttachmentStates.completed,
  ownerType = null,
  ownerId = null,
} = {}) {
  if (!sessionKey) return [];
  ensureStarted();

  const workspaceId = currentWorkspaceId();
  if (!workspaceId) return [];
  const records = await mediaAttachmentsByWorkspaceAndSession(workspaceId, sessionKey);
  if (!records.length) return [];

  const pending = records
    .filter((record) => {
      if (record.channel_id != channelId) return false;
      if (!matchesMediaAttachmentOwner(record, { ownerType, ownerId })) return false;
      // `uploadState: null` = all rows regardless of upload state. Channel sends use
      // this so the optimistic message can adopt unfinished uploads after submit.
      if (uploadState == null) return true;
      return record.upload?.state === uploadState;
    })
    .sort(sortByTimestamp('written_at'));

  return pending;
}

// TODO: remove this, not used?
export async function resetMediaAttachments() {
  ensureStarted();

  retryTimeouts.forEach((_scheduled, uploadId) => clearRetryTimeout(uploadId));
  await db.media_attachments.clear();
  try {
    await db._pending_upload_blobs.clear();
  } catch (error) {
    logWarn(STORE_TAG, 'failed to clear blob storage', { error });
  }
}

function buildExistingMediaAttachmentRecord({ attachment, sessionKey, channelId, workspaceId, ownerType = null, ownerId = null }) {
  return {
    ...attachment,
    id: attachment.id,
    workspace_id: workspaceId,
    attachment_id: attachment.id,
    upload: { state: MediaAttachmentStates.completed, progress: 100 },
    retry_count: 0,
    next_retry_at: null,
    resume_state: null,
    session_key: sessionKey,
    owner_type: ownerType,
    owner_id: ownerId,
    channel_id: channelId,
    // TODO: we might want to introduce an actual sort order, but for now we'll just use the attachment created_at
    // when figuring out how to sort on the client.
    // For a set of files uploaded call from scratch we have more control over setting `written_at`.
    written_at: attachment.created_at,
  };
}

export async function syncExistingMediaAttachments({ attachments = [], sessionKey, channelId, ownerType = null, ownerId = null }) {
  if (!sessionKey) return;
  ensureStarted();

  const workspaceId = currentWorkspaceId();
  if (!workspaceId) return;
  const normalizedAttachments = Array.isArray(attachments) ? attachments : [];
  const desiredIds = new Set(normalizedAttachments.map((attachment) => attachment.id));

  return db
    .transaction('rw', db.media_attachments, db._pending_upload_blobs, async () => {
      logDebug('dexie', 'starting media attachments transaction');

      const existingRecords = await mediaAttachmentsByWorkspaceAndSession(workspaceId, sessionKey);

      const existingByAttachmentId = new Map();
      existingRecords
        .filter((record) => record?.attachment_id)
        .forEach((record) => {
          existingByAttachmentId.set(record.attachment_id, record);
        });

      const removalCandidates = existingRecords.filter((record) => record?.attachment_id && !desiredIds.has(record.attachment_id));
      await removeMediaAttachmentsBulk(removalCandidates);

      for (let index = 0; index < normalizedAttachments.length; index += 1) {
        const attachment = normalizedAttachments[index];
        const attachmentId = attachment?.id;
        if (!attachmentId) continue;

        const existing = existingByAttachmentId.get(attachmentId);

        if (existing) {
          const updatedRecord = buildExistingMediaAttachmentRecord({
            attachment,
            sessionKey,
            channelId,
            workspaceId,
            ownerType,
            ownerId,
          });
          await db.media_attachments.put({ ...existing, ...updatedRecord });
          continue;
        }

        const record = buildExistingMediaAttachmentRecord({ attachment, sessionKey, channelId, workspaceId, ownerType, ownerId });
        await db.media_attachments.put(record);
      }
    })
    .then(() => {
      logDebug('dexie', 'finished media attachments transaction');
    });
}

export async function retryMediaAttachmentsByUploadIds(uploadIds = []) {
  const normalizedUploadIds = Array.isArray(uploadIds) ? uploadIds.filter(Boolean) : [];
  if (!normalizedUploadIds.length) return;
  ensureStarted();

  for (const uploadId of normalizedUploadIds) {
    const existing = await db.media_attachments.get(uploadId);
    if (!existing || existing.upload?.state === MediaAttachmentStates.completed) continue;

    // Reset to `retrying` with the best-guess resume point and let the pipeline pick it up.
    // The stored URL is dropped so the attempt always re-signs rather than replaying a
    // URL that may already have been rejected.
    await updateMediaAttachment(uploadId, {
      error: null,
      next_retry_at: null,
      resume_state: inferUploadResumeState(existing),
      presigned_upload_url: null,
      presign_attempts: 0,
      upload: {
        ...existing.upload,
        state: MediaAttachmentStates.retrying,
      },
    });

    await startUpload(uploadId);
  }
}

// A 4xx on the presigned PUT means the URL no longer matches (expired, or the signed
// headers drifted), not that the file is bad. Drop the URL so the next attempt re-signs
// the same remote record, bounded so a persistent rejection still surfaces.
function isRejectedPresignedPut(error) {
  const status = extractUploadErrorStatus(error);
  return error?.stage === 'put' && status !== null && status >= 400 && status < 500;
}

async function represignAfterRejectedPut(uploadId, error) {
  if (!isRejectedPresignedPut(error)) return false;

  const existing = await db.media_attachments.get(uploadId);
  if (!existing) return false;
  const attempts = Number(existing.presign_attempts) || 0;
  if (attempts >= MAX_PRESIGN_ATTEMPTS) return false;

  await updateMediaAttachment(uploadId, { presigned_upload_url: null, presign_attempts: attempts + 1 });
  await retryMediaAttachment(uploadId, error, MediaAttachmentStates.pending);
  return true;
}

async function startUpload(uploadId) {
  if (!uploadId) return null;
  ensureStarted();
  clearRetryTimeout(uploadId);

  if (inflightUploads.has(uploadId)) return inflightUploads.get(uploadId);

  const promise = runUploadPipeline(uploadId)
    .catch(async (error) => {
      if (isRetryableUploadError(error)) {
        // Retryable — keep the attachment row alive so the message can keep waiting on it.
        await retryMediaAttachment(uploadId, error, error?.resumeState || MediaAttachmentStates.pending);
        logWarn(STORE_TAG, 'pipeline scheduled for retry', { uploadId, error });
        return null;
      }

      if (await represignAfterRejectedPut(uploadId, error)) {
        logWarn(STORE_TAG, 'presigned upload rejected, re-signing', { uploadId, error });
        return null;
      }

      await failMediaAttachment(uploadId, error);
      logError(STORE_TAG, 'pipeline failed', { uploadId, error });
      throw error;
    })
    .then(
      (result) => {
        // Wait for the final state to land in Dexie before dropping the guard,
        // otherwise liveQuery can re-trigger the pipeline.
        inflightUploads.delete(uploadId);
        return result;
      },
      (error) => {
        inflightUploads.delete(uploadId);
        throw error;
      },
    );

  inflightUploads.set(uploadId, promise);
  return promise;
}

async function runUploadPipeline(uploadId) {
  // refresh the media attachment record and reset any existing errors
  const record = await refreshRecord(uploadId);
  if (!record.purpose) throw new Error('missing-purpose');
  const isRetryAttempt = record.upload?.state === MediaAttachmentStates.retrying;

  const resumeState = isRetryAttempt
    ? record.resume_state || MediaAttachmentStates.pending
    : record.upload?.state || MediaAttachmentStates.pending;

  await updateMediaAttachment(uploadId, {
    error: null,
    next_retry_at: null,
    resume_state: resumeState,
    // Keep visible state as `retrying` until the request makes progress — avoids
    // flashing the spinner on quick retry hops.
    upload: isRetryAttempt
      ? { ...record.upload }
      : {
          ...record.upload,
          state: resumeState,
        },
  });

  // File already uploaded — skip to polling for server-side processing.
  if (resumeState === MediaAttachmentStates.processing && record.attachment_id) {
    return runUploadStage(MediaAttachmentStates.processing, () => pollRemoteAttachment(uploadId, record.attachment_id));
  }

  const file = await getUploadFile(uploadId);
  if (!file) throw new Error('file-unavailable');

  // Stage 1: create the remote attachment to get an attachment id + presigned URL.
  const remote = await runUploadStage(MediaAttachmentStates.pending, () => ensureRemoteAttachment(uploadId, record));
  if (!remote?.id) throw new Error('missing-attachment-id');

  // Stage 2: upload the file to object storage via the presigned URL.
  await runUploadStage(MediaAttachmentStates.uploading, () =>
    uploadToPresignedUrl(uploadId, remote.presigned_upload_url, file, record?.metadata?.content_type),
  );

  if (remote.processing_mode === 'cloudflare_image') {
    await setMediaAttachmentProcessing(uploadId);
    const processed = await runUploadStage(MediaAttachmentStates.processing, () => processRemoteAttachment(remote.id));
    if (isProcessingComplete(processed)) return completeRemoteAttachment(uploadId, remote.id, processed);

    return runUploadStage(MediaAttachmentStates.processing, () => pollRemoteAttachment(uploadId, remote.id));
  }

  const lambdaPayload = remote.lambda_payload || record.lambda_payload;
  const processMediaAttachmentUrl = getMetaContent('upload_pipeline:process_media_attachment_url');
  if (isRelayableMediaContentType(record?.metadata?.content_type) && lambdaPayload && processMediaAttachmentUrl) {
    // If the page reloads while the Lambda request is in flight, the resumed pipeline
    // deliberately uses the established polling path instead of attempting a stale relay.
    await setMediaAttachmentProcessing(uploadId);

    const relay = await relayMediaAttachmentProcessing({
      attachmentId: remote.id,
      lambdaPayload,
      processMediaAttachmentUrl,
      isProcessingComplete,
      patchRequest: apiPatch,
    });

    if (relay.status === 'completed') {
      logDebug(STORE_TAG, 'media processing relay completed', { attachmentId: remote.id });
      return completeRemoteAttachment(uploadId, remote.id, relay.attachment);
    }

    if (relay.status === 'fallback') {
      logWarn(STORE_TAG, 'media processing relay fell back', {
        attachmentId: remote.id,
        reason: relay.reason,
        status: relay.responseStatus,
      });
    }
  }

  // Start processing now instead of when the B2 notification lands (see pingRemoteProcessing).
  pingRemoteProcessing(remote.id);

  // Stage 3: poll until server-side processing completes (e.g. image variant generation).
  await setMediaAttachmentProcessing(uploadId);

  return runUploadStage(MediaAttachmentStates.processing, () => pollRemoteAttachment(uploadId, remote.id));
}

async function rememberRemoteAttachment(uploadId, remote) {
  await updateMediaAttachment(uploadId, {
    ...remote,
    attachment_id: remote.id,
    presigned_upload_url: remote.presigned_upload_url,
    next_retry_at: null,
    resume_state: MediaAttachmentStates.uploading,
    upload: { state: MediaAttachmentStates.uploading, progress: 0 },
  });
}

// Re-signs the existing remote record instead of creating a replacement, which would
// leave the original behind as an orphan. Null when the server no longer has the
// record (purged as pending), so the caller falls back to a fresh create.
async function represignRemoteAttachment(uploadId, record) {
  logWarn(STORE_TAG, 'presigned url missing, expiring, or rejected; re-signing', { uploadId, attachmentId: record.attachment_id });

  const response = await apiPost(`/media_attachments/${record.attachment_id}/presign`);
  if (readResponseStatus(response) === 404) return null;

  const remote = await parseMediaAttachmentResponse(response, {
    defaultErrorMessage: 'presign-failed',
    invalidResponseMessage: 'invalid-presign-response',
    resumeState: MediaAttachmentStates.pending,
  });
  if (!remote.presigned_upload_url) throw new Error('invalid-presign-response');

  await rememberRemoteAttachment(uploadId, remote);
  return remote;
}

async function ensureRemoteAttachment(uploadId, record) {
  if (record.attachment_id) {
    const urlUsable =
      record.presigned_upload_url && isPresignedUrlUsable(record.presigned_upload_url, { minRemainingMs: PRESIGNED_URL_MIN_REMAINING_MS });
    if (urlUsable) {
      return {
        id: record.attachment_id,
        presigned_upload_url: record.presigned_upload_url,
        processing_mode: record.processing_mode,
        lambda_payload: record.lambda_payload,
      };
    }

    const represigned = await represignRemoteAttachment(uploadId, record);
    if (represigned) return represigned;
  }

  const response = await apiPost('/media_attachments', {
    workspace_id: record.workspace_id,
    media_attachment: {
      purpose: record.purpose,
      file_name: record?.metadata?.file_name ?? null,
      content_type: record?.metadata?.content_type ?? null,
      content_length: record?.metadata?.content_length ?? null,
      thumbhash: record?.metadata?.thumbhash ?? null,
      thumbsize: record?.metadata?.thumbsize ?? null,
      image_delivery_capability: 'cloudflare_v1',
    },
  });

  const remote = await parseMediaAttachmentResponse(response, {
    defaultErrorMessage: 'create-failed',
    invalidResponseMessage: 'invalid-create-response',
    resumeState: MediaAttachmentStates.pending,
  });
  if (!remote.presigned_upload_url) throw new Error('invalid-create-response');

  await rememberRemoteAttachment(uploadId, remote);
  return remote;
}

async function uploadToPresignedUrl(uploadId, url, file, contentType) {
  if (!url) throw new Error('missing-presigned-url');
  if (!file) throw new Error('file-unavailable');

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        setMediaAttachmentUploadingProgress(uploadId, progress).catch((error) => {
          logWarn(STORE_TAG, 'failed to record upload progress', { uploadId, progress, error });
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          buildUploadError(`upload-failed-${xhr.status}`, {
            status: xhr.status,
            retryable: isRetryableUploadStatus(xhr.status),
            resumeState: MediaAttachmentStates.uploading,
            stage: 'put',
          }),
        );
      }
    });

    xhr.addEventListener('error', () =>
      reject(
        buildUploadError('upload-network-error', {
          retryable: true,
          resumeState: MediaAttachmentStates.uploading,
        }),
      ),
    );

    xhr.open('PUT', url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);

    xhr.setRequestHeader('Content-Disposition', contentDispositionHeader(contentType, file.name));

    xhr.send(file);
  });
}

// Tells the server the bytes have landed so variant processing starts immediately rather than
// waiting on the B2 event notification (1-3s behind). Fire-and-forget by design: the webhook is
// still the backstop, so a failure here must never fail or retry the upload.
function pingRemoteProcessing(attachmentId) {
  if (!attachmentId) return;

  apiPost(`/media_attachments/${attachmentId}/process`)
    .then((response) => {
      if (response.ok) return;
      logWarn(STORE_TAG, 'process ping rejected', { attachmentId, status: readResponseStatus(response) });
    })
    .catch((error) => {
      logWarn(STORE_TAG, 'process ping failed', { attachmentId, error });
    });
}

async function processRemoteAttachment(attachmentId) {
  const response = await apiPost(`/media_attachments/${attachmentId}/process`);
  return parseMediaAttachmentResponse(response, {
    defaultErrorMessage: 'process-failed',
    invalidResponseMessage: 'invalid-process-response',
    resumeState: MediaAttachmentStates.processing,
  });
}

async function fetchRemoteAttachment(attachmentId) {
  if (!attachmentId) throw new Error('missing-attachment-id');

  const response = await apiGet(`/media_attachments/${attachmentId}`);
  return parseMediaAttachmentResponse(response, {
    defaultErrorMessage: 'show-failed',
    invalidResponseMessage: 'invalid-show-response',
    resumeState: MediaAttachmentStates.processing,
  });
}

// These can render from `public_url`, so the UI should not wait on async variants.
const PURPOSES_RENDERABLE_FROM_ORIGINAL = new Set(['workspace_background_image', 'workspace_flexicon', 'channel_flexicon']);

function expectsVariants(attachment) {
  if (PURPOSES_RENDERABLE_FROM_ORIGINAL.has(attachment?.purpose)) return false;

  const contentType = attachment?.metadata?.content_type;
  if (!contentType) return false;
  return (
    isImageContentType(contentType) ||
    contentType === 'application/pdf' ||
    contentType.startsWith('video/') ||
    contentType.startsWith('audio/')
  );
}

function hasRenderableVariant(attachment) {
  const variants = attachment?.variants;
  if (!variants) return false;

  const contentType = attachment?.metadata?.content_type;
  // Images and PDFs need an image variant, not just metadata. Some image purposes
  // emit nonstandard renderable variants, such as square_* flexicons or emoji.
  if (isImageContentType(contentType) || contentType === 'application/pdf') {
    return Boolean(
      variants.thumb || variants.medium || variants.square_thumb || variants.square_medium || variants.square_large || variants.emoji,
    );
  }
  // Video/audio need a mux variant
  if (contentType?.startsWith('video/') || contentType?.startsWith('audio/')) {
    return Boolean(variants.mux);
  }
  return Object.keys(variants).length > 0;
}

function isProcessingComplete(attachment) {
  if (!expectsVariants(attachment)) return true;
  return hasRenderableVariant(attachment);
}

async function setMediaAttachmentProcessing(uploadId) {
  await updateMediaAttachment(uploadId, {
    next_retry_at: null,
    resume_state: MediaAttachmentStates.processing,
    upload: { state: MediaAttachmentStates.processing, progress: 100 },
  });
}

async function completeRemoteAttachment(uploadId, attachmentId, attachment) {
  const workspaceId = resolveWorkspaceId(uploadId);
  const completedAttachmentId = attachment.id || attachmentId;
  await updateMediaAttachment(uploadId, {
    ...attachment,
    attachment_id: completedAttachmentId,
    error: null,
    next_retry_at: null,
    resume_state: null,
    upload: { state: MediaAttachmentStates.completed, progress: 100 },
  });

  await deleteBlobStorage(workspaceId, uploadId);
  logDebug(STORE_TAG, 'upload completed', { uploadId, attachmentId: completedAttachmentId });
  return attachment;
}

async function pollRemoteAttachment(uploadId, attachmentId) {
  let pollAttempt = 0;
  while (true) {
    if (!(await db.media_attachments.get(uploadId))) return null;

    const attachment = await fetchRemoteAttachment(attachmentId);
    const completed = isProcessingComplete(attachment);

    if (completed) {
      return completeRemoteAttachment(uploadId, attachmentId, attachment);
    }

    await updateMediaAttachment(uploadId, {
      ...attachment,
      attachment_id: attachment.id,
      error: null,
      next_retry_at: null,
      resume_state: MediaAttachmentStates.processing,
      upload: { state: MediaAttachmentStates.processing, progress: 100 },
    });

    // Reload-resumed processing starts a fresh invocation, so the counter
    // restarts at the fast interval without any persisted state.
    const delayIndex = Math.min(pollAttempt, REMOTE_ATTACHMENT_POLL_INTERVALS_MS.length - 1);
    pollAttempt += 1;
    await wait(REMOTE_ATTACHMENT_POLL_INTERVALS_MS[delayIndex]);
  }
}

async function refreshRecord(uploadId) {
  ensureStarted();

  const record = await db.media_attachments.get(uploadId);
  if (!record) throw new Error('upload-missing');
  return record;
}
